import React from 'react';
import { AddressTypes, ListActionSelect } from '@sweetgreen/garnish';

import { RestaurantListAction } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { mapAvailableTimes } from '../../../Checkout.utils';
import { useFirstAvailableOption } from '../../../hooks/useFirstAvailableOption/useFirstAvailableOption';
import { CheckoutBlockVStack } from '../CheckoutBlockVStack';
import type { CheckoutDetailsBlockProps } from './CheckoutDetailsBlock.types';

export const PickupOptions = React.memo(
  ({
    orderId,
    orderType,
    loadingAvailableWantedTimes,
    availableWantedTimeOptions,
    restaurant,
    selectedTime,
    setSelectedTime,
    dropoffLocation,
    setDropoffLocation,
    resetCheckoutFieldValidation,
    checkoutFieldsValidation,
  }: CheckoutDetailsBlockProps) => {
    const { t } = useLocalizationContext();
    const timeOptionsGroups = React.useMemo(
      () =>
        mapAvailableTimes({
          t,
          orderType,
          times: availableWantedTimeOptions,
        }),
      [t, orderType, availableWantedTimeOptions],
    );

    useFirstAvailableOption({
      timeOptionsGroups,
      selectedTime,
      setSelectedTime,
    });

    // ──────── Telemetry ─────────

    const { track } = useTelemetry();

    const onTimeChange = React.useCallback(
      (time: string) => {
        const delta =
          new Date(time).getTime() - new Date(selectedTime).getTime();

        track('checkout.time_changed', { timeChanged: delta / (60 * 1000) });

        setSelectedTime(time);
      },
      [selectedTime, setSelectedTime, track],
    );

    const onPickupSelectorChange = React.useCallback(
      (e) => {
        setDropoffLocation(e);
        resetCheckoutFieldValidation('dropoffLocationId');

        track('checkout.pickup_selector_changed', {
          pickupChanged: dropoffLocation,
        });
      },
      [
        dropoffLocation,
        resetCheckoutFieldValidation,
        setDropoffLocation,
        track,
      ],
    );

    // ────────────────────────────

    const availableDropOffLocations = React.useMemo(() => {
      return restaurant?.availableDropOffLocations ?? [];
    }, [restaurant?.availableDropOffLocations]);

    // ──────────────────────────── PICKUP SELECTOR ────────────────────────────
    const isPickUpSelectorEnabled = useFeatureFlag('pickup-location-selector', {
      listenForChanges: true,
    });

    const isDropOffLocationFieldValid =
      checkoutFieldsValidation.dropoffLocationId;
    const dropOffLocationFieldNotice = isDropOffLocationFieldValid
      ? undefined
      : t('checkout.required-field');

    // ────────────────────────────

    if (orderType !== AddressTypes.pickup) return null;

    return (
      <CheckoutBlockVStack testID="checkout-block-pickup">
        <ListActionSelect
          testID="checkout.pickup-time"
          iconName="IconClock"
          title={t('checkout.pickup-time')}
          modalTitle={t('checkout.choose-pickup-time')}
          value={selectedTime}
          onSelect={onTimeChange}
          options={timeOptionsGroups}
          isLoading={loadingAvailableWantedTimes}
        />
        <RestaurantListAction
          orderId={orderId}
          restaurant={restaurant}
          orderType={orderType}
        />
        {isPickUpSelectorEnabled &&
        (restaurant?.availableDropOffLocations ?? []).length > 1 ? (
          <ListActionSelect
            iconName="IconFloor"
            testID="checkout.pick-up-location"
            title={t('checkout.pickup-location')}
            modalTitle={t('checkout.choose-pickup-location')}
            value={dropoffLocation ?? 'placeholder'}
            onSelect={onPickupSelectorChange}
            options={[
              {
                value: 'placeholder',
                label: t('checkout.choose-pickup-location'),
              },
              ...availableDropOffLocations.map((location) => ({
                value: location.id,
                label: location.name,
              })),
            ]}
            notice={dropOffLocationFieldNotice}
            noticePalette="caution"
          />
        ) : null}
      </CheckoutBlockVStack>
    );
  },
);
